import { Col, Collapse, Image, Row, Tabs } from "antd";
import { useCallback, useEffect, useRef } from "react";
import { useGlobalDownloader } from "../../../context/global/DownloadProvider";
import { useMenuContext } from "../../../context/MenuContext";
import { useMediaResource } from "../../../hooks/useMediaResource";
import MediaViewerModal from "../../../components/modal/MediaViewerModal";
import { ActionMenu, FileItem } from "./PanelView";

const ImagePanel = ({
  resourceList,
  jumpToMessage,
  isLastGroup,
  isMail = false,
}) => {
  const downloader = useGlobalDownloader();
  const lastObserver = useRef(null);
  const mediaResource = useMediaResource();
  const {
    mediaStorageLoading,
    loadMoreMediaStorage,
    fetchMediaStorageResources,
  } = useMenuContext();

  const lastElementRef = useCallback(
    (node) => {
      lastObserver.current?.disconnect();
      if (!node || !isLastGroup || !loadMoreMediaStorage) return;

      const scrollContainer = node.closest("[data-media-scroll-container]");
      lastObserver.current = new IntersectionObserver(
        async ([entry]) => {
          if (
            !entry.isIntersecting ||
            mediaStorageLoading ||
            !loadMoreMediaStorage
          ) {
            return;
          }
          lastObserver.current?.disconnect();
          await fetchMediaStorageResources?.("desc");
        },
        { root: scrollContainer, rootMargin: "24px 0px", threshold: 0 },
      );
      lastObserver.current.observe(node);
    },
    [
      mediaStorageLoading,
      fetchMediaStorageResources,
      isLastGroup,
      loadMoreMediaStorage,
    ],
  );

  const openModal = (image) => {
    mediaResource.setSelectedMedia(image);
    mediaResource.setMediaTypes(["IMAGE"]);
    mediaResource.setIsFetchData(true);
    mediaResource.setVisible(true);
  };

  if (!resourceList?.length) return null;

  return (
    <Row>
      {resourceList.map((image, index) => (
        <Col
          key={
            image?.mainPath ||
            image?.thumbnailPreviewPath ||
            image?.resourceId ||
            image?.id ||
            index
          }
          span={8}
          className="p-[3px] h-[70px] parent-action-menu"
          ref={
            isLastGroup && index === resourceList.length - 1
              ? lastElementRef
              : null
          }
        >
          <Image
            src={
              image?.thumbnailPreviewPath ||
              image?.mainPath ||
              image?.pathUrl ||
              image?.location ||
              image?.media?.thumbnailPreviewPath ||
              image?.media?.mainPath
            }
            className="rounded cursor-pointer object-cover border border-gray-300"
            preview={false}
            width="100%"
            height="100%"
            onClick={() => openModal(image)}
          />
          <ActionMenu
            file={image}
            downloader={downloader}
            jumpToMessage={jumpToMessage}
            isMail={isMail}
          />
        </Col>
      ))}
      {mediaResource.visible && (
        <MediaViewerModal mediaResource={mediaResource} />
      )}
    </Row>
  );
};

const FilePanel = ({ resourceList, jumpToMessage, isMail = false }) => {
  if (!resourceList?.length) return null;
  return resourceList.map((file, index) => (
    <FileItem
      key={file?.mainPath || file?.resourceId || file?.messageId || index}
      file={file}
      idx={index}
      jumpToMessage={jumpToMessage}
      isMail={isMail}
    />
  ));
};

const MediaStorageView = ({ jumpToMessage, isMail = false }) => {
  const {
    selectedConversation,
    mediaStorageType = "IMAGE",
    setMediaStorageType,
    collapseMedia,
    openCollapses,
    setOpenCollapses,
    fetchMediaStorageResources,
  } = useMenuContext();

  useEffect(() => {
    fetchMediaStorageResources?.();
  }, [mediaStorageType, selectedConversation?.conversationId]);

  const collapseItems = (collapseMedia || []).map((group, index) => ({
    ...group,
    children:
      mediaStorageType === "IMAGE" ? (
        <ImagePanel
          resourceList={group.files}
          jumpToMessage={jumpToMessage}
          isLastGroup={index === (collapseMedia || []).length - 1}
          isMail={isMail}
        />
      ) : (
        <FilePanel
          resourceList={group.files}
          jumpToMessage={jumpToMessage}
          isMail={isMail}
        />
      ),
  }));

  return (
    <>
      <Tabs
        activeKey={mediaStorageType}
        onChange={setMediaStorageType}
        items={[
          { key: "IMAGE", label: "Images" },
          { key: "VIDEO", label: "Videos" },
          { key: "FILE", label: "Files" },
          { key: "AUDIO", label: "Audios" },
        ]}
      />
      <div className="h-full overflow-auto" data-media-scroll-container>
        <Collapse
          items={collapseItems}
          bordered={false}
          activeKey={openCollapses}
          onChange={setOpenCollapses}
        />
      </div>
    </>
  );
};

export { MediaStorageView };
export default MediaStorageView;
